import { useMemo, useState } from "react";
import { object } from "prop-types";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { PriceModifiers, PriceModifierModes } from "@repo/utils";
import { FC_PREFIX, MAT_PREFIX } from "@repo/structure";
import { useEstimateStructure } from "@tools";
import { Button, Dialog, TooltipIcon } from "@components";
import Grid from "@material-ui/core/Grid";
import { PercentSettingContents } from "@tools";
import { PriceModifierTitle, FixedPriceInput, ModeSwitch } from "./components";
import { Settings } from "@assets";

export const PriceModifierManager = observer(({
  priceModifier,
}) => {
  const structure = useEstimateStructure();
  const { t } = useTranslation();
  
  const {
    type,
    mode,
    name,
    percent,
    ofEntities,
    fixedPrice,
  } = priceModifier;
  
  const [ visible, show ] = useState(false);
  
  const typeKey = useMemo(() => (
    type === PriceModifiers.DISCOUNT ? "discount" : "overhead"
  ), []);
  
  const availableWorkTypes = useMemo(() => {
    let list = [ ...structure.workTypes ];
    
    if(structure.hasBreakdownFixedPrice)
      list.push({ id: FC_PREFIX, name: t("views.editor.summary_table.fixed_costs")});
    if(structure.hasBreakdownMaterialPrice)
      list.push({ id: MAT_PREFIX, name: t("views.editor.summary_table.materials") });

    return list;
  }, [ structure.workTypes, structure.hasBreakdownFixedPrice, structure.hasBreakdownMaterialPrice ]);
  
  const handleOpen = async () => {
    show(true);
  }
  
  const handleClose = () => {
    show(false);
  }
  
  const handleSetMode = (mode) => {
    priceModifier.setMode(mode)
  }
  
  const handleSetPercent = (newPercent) => {
    // Ensure element always uses positive/negative percent values
    const modifiedPercent = type === PriceModifiers.DISCOUNT
      ? -Math.abs(newPercent)
      : Math.abs(newPercent);
    priceModifier.setPercent(modifiedPercent);
  }
  
  const handleSetFixedPrice = (newValue) => {
    // Ensure element always uses positive/negative percent values
    const modifiedValue = type === PriceModifiers.DISCOUNT
      ? -Math.abs(newValue)
      : Math.abs(newValue);
    priceModifier.setFixedPrice(modifiedValue);
  }
  
  return (
    <>
      <TooltipIcon
        icon={<Settings />}
        onClick={handleOpen}
        color="primary"
        className={`button-${typeKey}-settings noDrag hoverIcon opq`}
      >
        {t(`views.editor.dialogs.price_modifiers.edit.${typeKey}`)}
      </TooltipIcon>
      {
        visible &&
        <Dialog
          open
          onClose={handleClose}
          width={624}
          title={t(`views.editor.dialogs.price_modifiers.edit.${typeKey}`)}
          actions={
            <>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleClose}
              >
                {t("common.close")}
              </Button>
              {/*<Button*/}
              {/*  variant="contained"*/}
              {/*  color="primary"*/}
              {/*  onClick={addNewPriceModifier}*/}
              {/*>*/}
              {/*  {t("common.apply")}*/}
              {/*</Button>*/}
            </>
          }
        >
          <Grid item container direction="column">
            <PriceModifierTitle
              name={name}
              type={type}
              percent={percent}
              setName={priceModifier.setName}
            />
            <ModeSwitch
              value={mode}
              onChange={handleSetMode}
            />
            {
              mode === PriceModifierModes.PERCENT
                ? <PercentSettingContents
                  percent={percent}
                  usedWorkTypes={ofEntities.slice()}
                  availableWorkTypes={availableWorkTypes}
                  onPercentChange={handleSetPercent}
                  onSelectWorkType={priceModifier.setEntities}
                  hideWorkTypeList
                  allowSelectAllWorkTypes
                  minValue={0.01}
                  maxValue={999}
                  prefix={type === PriceModifiers.DISCOUNT ? "-" : undefined}
                />
                : <FixedPriceInput
                  value={Math.abs(fixedPrice)}
                  onChange={handleSetFixedPrice}
                  prefix={type === PriceModifiers.DISCOUNT ? "-" : undefined}
                />
            }
          </Grid>
        </Dialog>
      }
    </>
  );
});

PriceModifierManager.propTypes = {
  priceModifier: object.isRequired,
}